import Meta from '../../components/Meta';
import AdminCopilotChat from '../../components/AdminCopilotChat/AdminCopilotChat';
import './AdminAgentScreen.less';
import { Spin, Splitter } from 'antd';
import { useEffect, useState } from 'react';
import AdminCopilotResults from '../../components/AdminCopilotResults/AdminCopilotResults';
import { useSidebarMenuItem } from '../../hooks/useSidebarMenuItem';
import { useLocation, useNavigationType, useParams } from 'react-router-dom';
import { useAgentSelector } from '../../hooks/useAgentStore';
import NewChatWelcomeScreen from '../../components/NewChatWelcomeScreen/NewChatWelcomeScreen';
import { AppRoutes } from '../../constants/route';
import { FAILED_THREAD_ID } from '../../constants/adminAgent';

const AdminAgentScreen: React.FC = () => {
  const [sizes, setSizes] = useState<(number | string)[]>(['100%', '0%']);
  const [key, setKey] = useState<string>(Date.now().toString());
  const { threadId } = useParams<{ threadId: string }>();
  const {
    isLoading,
    addNewChatToThreadList,
    moveThreadToTop,
    setStatesForNewChat,
    setStatesForExistingChat,
    navigateToNewChat,
  } = useSidebarMenuItem();
  const { result } = useAgentSelector((state) => state.adminAgent);
  const [isNewChatMode, setIsNewChatMode] = useState(!threadId);
  const [newChatFirstMessage, setNewChatFirstMessage] = useState('');
  const [isResultPanelVisible, setIsResultPanelVisible] = useState(false);
  const [showResultButton, setShowResultButton] = useState(
    result && result.length > 0 ? true : false
  );
  const location = useLocation();
  const navigationType = useNavigationType();
  const hasThreadError = threadId === FAILED_THREAD_ID;

  useEffect(() => {
    if (navigationType !== 'REPLACE') {
      if (hasThreadError) {
        setStatesForNewChat();
        navigateToNewChat();
      } else if (threadId) {
        setStatesForExistingChat(threadId);
      }
    }
  }, [location, navigationType, threadId]);

  const handleRequestCallback = async (message: string) => {
    if (message) {
      setNewChatFirstMessage(message);
      setIsNewChatMode(false);
    }
  };

  const showResultPanel = () => {
    setSizes(['50%', '50%']);
    setIsResultPanelVisible(true);
    setShowResultButton(false);
  };

  useEffect(() => {
    if (!threadId) {
      setKey(Date.now().toString());
      setIsNewChatMode(true);
    } else {
      setIsNewChatMode(false);
    }
    setNewChatFirstMessage('');
  }, [threadId]);

  useEffect(() => {
    const resultPanelVisible = result && result.length > 0 ? true : false;
    setIsResultPanelVisible(resultPanelVisible);
    setShowResultButton(resultPanelVisible);
    if (resultPanelVisible) {
      setSizes(['50%', '50%']);
    } else {
      setSizes(['100%', '0%']);
    }
  }, [result]);

  return (
    <>
      <Meta name={`Cirra AI - ${AppRoutes.adminAgent.label}`} />
      <div key={key} className="main-screen-element" id="admin-agent-screen">
        <div className="admin-agent-main-content">
          {isLoading ? (
            <div className="loading-container">
              <Spin />
            </div>
          ) : isNewChatMode ? (
            <NewChatWelcomeScreen onSend={handleRequestCallback} />
          ) : (
            <div className="copilot-wrapper">
              {showResultButton && (
                <div className="show-results-button">
                  <button onClick={showResultPanel}>Show Results</button>
                </div>
              )}

              <Splitter
                onResize={setSizes}
                className="admin-agent-splitter"
                style={{
                  height: 'calc(100vh - 64px)', // Adjust based on header height
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Splitter.Panel
                  size={sizes[0]}
                  resizable={true}
                  className={`admin-agent-chat-panel ${
                    isResultPanelVisible ? 'collapsed' : 'expanded'
                  }`}
                >
                  <AdminCopilotChat
                    disabled={hasThreadError}
                    addNewChatToThreadList={addNewChatToThreadList}
                    updateThreads={moveThreadToTop}
                    newChatFirstMessage={newChatFirstMessage}
                    onShowResults={showResultPanel}
                    isResultPanelVisible={isResultPanelVisible}
                  />
                </Splitter.Panel>
                {isResultPanelVisible && (
                  <Splitter.Panel
                    size={sizes[1]}
                    className="admin-agent-results-panel"
                  >
                    <AdminCopilotResults
                      onClose={() => {
                        setShowResultButton(true);
                        setIsResultPanelVisible(false);
                      }}
                    />
                  </Splitter.Panel>
                )}
              </Splitter>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminAgentScreen;
